import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { ukDayYear, ukDayTime } from "@/lib/time";
import { toast } from "sonner";
import { SignatureField } from "@/components/portal/SignatureField";
import {
  Btn, CellMain, CellSub, DateCell, DeskSupport, Divider, Empty, Kv, Ladder, LinkRow, Money,
  Note, PageBody, PageHead, Panel, RailCard, Sec, St, Tb, Td, Th,
} from "@/components/portal/crm/kit";

const pull = async (path, filename, params) => {
  const res = await api.get(path, { responseType: "blob", params: { ...(params || {}), v: Date.now() } });
  const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
};

const STATE = (row) => {
  if (row.status === "signed" && row.current) return { tone: "green", label: "In force" };
  if (row.status === "signed") return { tone: "slate", label: "Superseded" };
  if (row.status === "sent") return { tone: "quiet", label: "Awaiting you" };
  return { tone: "slate", label: "Not issued yet" };
};

const Step = ({ n, done, now, label, title, body }) => (
  <div className={`st2${done ? " done" : now ? " now" : ""}`}>
    <div className="k2"><i>{done ? "✓" : n}</i>{label}</div>
    <div className="t2">{title}</div>
    <div className="d2">{body}</div>
  </div>
);

export default function Paperwork() {
  const { me, reload } = useOutletContext();
  const [docs, setDocs] = useState(null);
  const [rows, setRows] = useState([]);
  const [files, setFiles] = useState([]);
  const [name, setName] = useState("");
  const [signing, setSigning] = useState(false);
  const sigRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [d, ha, hn, pf] = await Promise.all([
        api.get("/portal/documents"),
        api.get("/portal/documents/history", { params: { doc: "agreement" } }).catch(() => ({ data: { items: [] } })),
        api.get("/portal/documents/history", { params: { doc: "nda" } }).catch(() => ({ data: { items: [] } })),
        api.get("/portal/partner-docs").catch(() => ({ data: { items: [] } })),
      ]);
      setDocs(d.data);
      setRows([
        ...(ha.data.items || []).map((r) => ({ ...r, doc: "agreement" })),
        ...(hn.data.items || []).map((r) => ({ ...r, doc: "nda" })),
      ]);
      setFiles(pf.data.items || []);
      setName((n) => n || d.data.party_name || "");
    } catch { setDocs(null); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const ag = docs?.agreement;
  const nda = docs?.nda;
  const kind = docs?.kind || "referrer";
  const title = ag?.title || (kind === "lender" ? "Panel agreement" : "Introducer agreement");
  const outstanding = useMemo(
    () => [["agreement", ag], ["nda", nda]].filter(([, v]) => v && v.status !== "signed").map(([k]) => k),
    [ag, nda],
  );
  const next = outstanding[0];
  const live = me?.status === "active" && ag?.status === "signed";
  /* Partner-facing pages state the rate in force and nothing else. Where a signed version was
     executed at an older rate the Hub flags it for re-issue — that is a desk job, not a caption. */
  const liveT = ag?.live_terms;

  const sign = async (doc) => {
    const typed = (name || "").trim();
    if (typed.length < 2) { toast.error("Type your full name as you would sign it"); return; }
    if (sigRef.current?.isEmpty?.()) { toast.error("Add your signature — type it or draw it"); return; }
    setSigning(true);
    try {
      await api.post(`/portal/${doc === "nda" ? "nda" : "agreement"}/sign`, {
        signed_name: typed,
        signature_png: sigRef.current.toDataURL(),
      });
      toast.success(doc === "nda" ? "NDA signed and countersigned — the PDF is on its way to you"
        : "Agreement signed and countersigned — the PDF is on its way to you");
      await load();
      reload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Signing failed — message the desk and we'll sort it"); }
    finally { setSigning(false); }
  };

  if (!docs) return <Empty testId="referrer-paperwork-loading">Loading your paperwork…</Empty>;

  const steps = [
    { state: "d", label: "Applied" },
    { state: nda?.status === "signed" ? "d" : "n", label: "NDA" },
    { state: ag?.status === "signed" ? "d" : nda?.status === "signed" ? "n" : "", label: "Agreement" },
    { state: live ? "d" : "", label: "Live" },
  ];

  return (
    <>
      <PageHead
        eyebrow="Paperwork"
        title="NDA &amp; agreement"
        lede="Both documents are signed here, in the portal, in about a minute. We countersign immediately and email the completed PDF to both sides. No printing, no posting, no third-party signing service."
        testId="referrer-paperwork-head"
        actions={next
          ? <Btn kind="primary" href="#sign" testId="paperwork-sign-cta">Review and sign {next === "nda" ? "the NDA" : "agreement"}</Btn>
          : <Btn onClick={() => pull("/portal/agreement.pdf", "connexis-agreement.pdf")} testId="paperwork-download-cta">Download your agreement</Btn>}
      />

      <div className="steps2" data-testid="paperwork-steps">
        <Step n="1" done label="Step one" title="Application received"
              body={me?.created_at ? `Joined ${ukDayYear(me.created_at)}.` : "On file with the desk."} />
        <Step n="2" done={nda?.status === "signed"} now={nda?.status !== "signed"} label="Step two" title="NDA signed"
              body={nda?.status === "signed"
                ? `Signed ${ukDayYear(nda.signed_at)}, countersigned the same day. Mutual, two years.`
                : "Mutual, two years. Sign it below — it takes about twenty seconds."} />
        <Step n="3" done={ag?.status === "signed"} now={ag?.status !== "signed" && nda?.status === "signed"}
              label="Step three" title={title}
              body={ag?.status === "signed"
                ? `Version ${ag.version} signed ${ukDayYear(ag.signed_at)}. ${liveT?.short || ag.terms?.short}.`
                : `Version ${ag?.version || 1}${ag?.sent_at ? ` issued ${ukDayYear(ag.sent_at)}` : ""}, waiting on your signature.`} />
        <Step n="4" done={live} label="Step four" title="Live partner"
              body={live ? "Links on, media kit built, cases flowing." : "Links switch on, media kit builds, cases can be submitted."} />
      </div>

      <div style={{ marginTop: 34 }}>
        <PageBody
          rail={
            <>
              <Panel title="Where you are" testId="paperwork-where">
                <Ladder steps={steps} />
                <Divider style={{ margin: "20px 0" }} />
                <p style={{ color: "var(--ink-3)", fontSize: 13, margin: 0 }}>
                  {ag?.status !== "signed"
                    ? <>You can look around the portal now, but <b>cases cannot be submitted</b> and your media kit stays locked until the agreement is signed.</>
                    : next
                      ? <>Your agreement is in force, so cases can be submitted. The <b>{next === "nda" ? "mutual NDA" : "agreement"}</b> is still waiting on your signature — it takes about twenty seconds.</>
                      : "Everything is signed and in force. A superseded version stays downloadable below for as long as you are a partner."}
                </p>
                {next && <Btn kind="primary" block style={{ marginTop: 16 }} href="#sign" testId="paperwork-where-sign">Sign it now</Btn>}
              </Panel>

              <DeskSupport body="Happy to walk through any clause, or send it to your solicitor first." />

              <RailCard title="Also on file" testId="paperwork-also-on-file">
                {files.length === 0 && (
                  <p style={{ color: "var(--mut)", fontSize: 12.5, margin: 0 }}>
                    Nothing else filed yet. Anything we countersign or you send us appears here.
                  </p>
                )}
                {files.map((f) => (
                  <LinkRow key={f.id} label={f.title || f.filename} testId={`paperwork-file-${f.id}`}
                           onClick={async () => {
                             try {
                               const r = await api.get(`/portal/partner-docs/${f.id}/download`, { responseType: "blob" });
                               const url = URL.createObjectURL(r.data);
                               const a = document.createElement("a");
                               a.href = url; a.download = f.filename || "document";
                               document.body.appendChild(a); a.click(); a.remove();
                               setTimeout(() => URL.revokeObjectURL(url), 2000);
                             } catch { toast.error("That file wouldn't open — tell the desk and we'll re-send it"); }
                           }} />
                ))}
              </RailCard>
            </>
          }
        >
          <Panel
            testId="paperwork-current"
            title={`${title}, version ${ag?.version || 1}`}
            sub={[ag?.sent_at ? `Issued ${ukDayYear(ag.sent_at)}` : null,
              ag?.superseded_count ? `replaces version ${(ag.version || 1) - 1}` : null].filter(Boolean).join(" · ")}
            right={ag?.status === "signed"
              ? <St tone="green" testId="paperwork-current-state">In force</St>
              : <St tone="quiet" testId="paperwork-current-state">Awaiting your signature</St>}
          >
            <div className="rail-t">What it says, in plain English</div>
            <Kv left style={{ marginTop: 14, maxWidth: 620, gap: "12px 26px" }} rows={[
              ["Your commission", liveT?.rate || ag?.terms?.rate || "—"],
              ["How it is worked out", liveT?.short || ag?.terms?.short || "—", { style: { fontSize: 13 } }],
              ["When we pay", liveT?.schedule || ag?.terms?.schedule || "—", { style: { fontSize: 13 } }],
              ["Exclusivity", "None — work with whoever you like", { style: { fontSize: 13 } }],
              ["Non-circumvention", "You do not approach our panel directly", { style: { fontSize: 13 } }],
              ["Notice to end it", "30 days, either side", { style: { fontSize: 13 } }],
              ["Deals already in flight", "Still paid after termination", { style: { fontSize: 13 } }],
              ["Who signs for Connexis", docs.connexis_signatory, { style: { fontSize: 13 } }],
            ]} />
            <Divider />
            <div style={{ display: "flex", gap: 11, flexWrap: "wrap" }}>
              {ag?.status !== "signed" && <Btn kind="primary" href="#sign" testId="paperwork-read-and-sign">Read it and sign</Btn>}
              <Btn onClick={() => pull("/portal/agreement.pdf", "connexis-agreement.pdf")} testId="paperwork-download-agreement">
                {ag?.status === "signed" ? "Download the signed PDF" : "Download the draft PDF"}
              </Btn>
              <Btn onClick={() => window.dispatchEvent(new CustomEvent("portal-chat:open"))} testId="paperwork-ask-desk">
                Ask the desk a question
              </Btn>
            </div>
            <p style={{ color: "var(--mut)", fontSize: 12.5, margin: "18px 0 0" }}>
              Nothing here is negotiable through this screen — if a term does not work for you, message the desk and
              we will reissue it. Your old agreement stays in force until you sign the new one.
            </p>
          </Panel>

          {ag?.status === "signed" && (
            <Sec>
              <Note testId="paperwork-terms-current">
                The rate shown here is the rate your portal, your statements and your payouts all use. If it
                ever changes we write to you first and re-issue the agreement on this page — and the version
                you signed stays in force until you sign the new one.
              </Note>
            </Sec>
          )}

          <Sec
            title="How signing works"
            sub="Type your name, draw your signature, done. We capture the time, your IP and the document version, and lock the PDF."
          >
            <Panel testId="paperwork-signing" style={{ scrollMarginTop: 90 }}>
              <span id="sign" />
              <div className="grid2" style={{ gap: 34 }}>
                <div className="sign">
                  <div className="rail-t">{next ? `Sign ${next === "nda" ? "the mutual NDA" : "in the portal"}` : "Everything is signed"}</div>
                  {next ? (
                    <>
                      <SignatureField ref={sigRef} name={name} onNameChange={setName} testId="paperwork-signature" />
                      <div style={{ display: "flex", gap: 9, marginTop: 16, flexWrap: "wrap" }}>
                        <Btn kind="primary" sm onClick={() => sign(next)} disabled={signing} testId="paperwork-sign-send">
                          {signing ? "Signing…" : "Sign and send"}
                        </Btn>
                        <Btn sm onClick={() => pull(next === "nda" ? "/portal/nda.pdf" : "/portal/agreement.pdf",
                          next === "nda" ? "connexis-nda.pdf" : "connexis-agreement.pdf")}
                             testId="paperwork-read-first">Read it first</Btn>
                      </div>
                      {outstanding.length > 1 && (
                        <p style={{ color: "var(--mut)", fontSize: 12.5, margin: "14px 0 0" }}>
                          The {next === "nda" ? "agreement" : "NDA"} comes straight after this one.
                        </p>
                      )}
                    </>
                  ) : (
                    <p style={{ color: "var(--ink-3)", fontSize: 13.5, margin: 0, lineHeight: 1.6 }}>
                      Your NDA and agreement are both executed and countersigned. Every version stays downloadable
                      below, and we will tell you in writing before anything changes.
                    </p>
                  )}
                </div>
                <div>
                  <div className="rail-t">Or send us a scan</div>
                  <p style={{ color: "var(--ink-3)", fontSize: 13.5, margin: "0 0 16px", lineHeight: 1.6 }}>
                    If your compliance team needs a wet signature, download the PDF, sign it, and send it to the desk
                    thread. We treat the scan as the executed copy and still countersign it.
                  </p>
                  <Btn onClick={() => window.dispatchEvent(new CustomEvent("portal-chat:open"))} testId="paperwork-send-scan">
                    Send a signed scan to the desk
                  </Btn>
                  <Divider style={{ margin: "20px 0" }} />
                  <div className="rail-t">Who signs for Connexis</div>
                  <CellMain style={{ fontSize: 15 }}>{(docs.connexis_signatory || "").split(" · ")[0]}</CellMain>
                  <CellSub>{(docs.connexis_signatory || "").split(" · ")[1] || "Connexis Group Ltd"}</CellSub>
                  <p style={{ color: "var(--mut)", fontSize: 12.5, margin: "14px 0 0" }}>
                    Countersigned automatically on receipt, then the completed PDF is emailed to both parties and
                    filed under Documents.
                  </p>
                </div>
              </div>
            </Panel>
          </Sec>

          <Sec
            title="Everything on file"
            sub="Nothing is ever overwritten. A superseded agreement stays downloadable for as long as you are a partner."
          >
            <Panel pad={false} testId="paperwork-history" style={{ overflow: "hidden" }}
                   footer={
                     <>
                       <span>Each PDF carries both signatures, the date, the version and an audit reference.</span>
                       <span style={{ fontWeight: 700, color: "var(--ink-3)" }}>{ukDayTime(new Date().toISOString())} UK</span>
                     </>
                   }>
              {rows.length === 0 ? (
                <Empty testId="paperwork-history-empty">Nothing issued yet — your paperwork appears here the moment the desk sends it.</Empty>
              ) : (
                <Tb testId="paperwork-history-table"
                    head={<><Th>Document</Th><Th>Status</Th><Th>Signed by you</Th><Th>Countersigned</Th><Th className="r">PDF</Th></>}>
                  {rows.map((r) => {
                    const st = STATE(r);
                    const label = r.doc === "nda" ? `Mutual NDA v${r.version}` : `${title} v${r.version}`;
                    return (
                      <tr key={`${r.doc}-${r.version}`} className={r.status === "sent" ? "flag-amber" : undefined}
                          data-testid={`paperwork-row-${r.doc}-${r.version}`}>
                        <Td className="lead">
                          <CellMain>{label}</CellMain>
                          <CellSub>
                            {r.current
                              ? (r.status === "signed" ? `${r.rate || ""} ${r.short ? `— ${r.short}` : ""}`.trim() || "Current version" : "Issued, waiting on you")
                              : `Superseded${r.superseded_at ? ` ${ukDayYear(r.superseded_at)}` : ""}`}
                          </CellSub>
                        </Td>
                        <Td><St tone={st.tone}>{st.label}</St></Td>
                        <Td>{r.signed_at ? <DateCell>{ukDayTime(r.signed_at)}</DateCell> : <Money raw="—" tone="muted" />}</Td>
                        <Td>{r.signed_at ? <DateCell>{ukDayTime(r.signed_at)}</DateCell> : <Money raw="—" tone="muted" />}</Td>
                        <Td className="r">
                          <Btn sm testId={`paperwork-pdf-${r.doc}-${r.version}`}
                               onClick={() => (r.current
                                 ? pull(r.doc === "nda" ? "/portal/nda.pdf" : "/portal/agreement.pdf", `connexis-${r.doc}-v${r.version}.pdf`)
                                 : pull(`/portal/documents/history/${r.version}.pdf`, `connexis-${r.doc}-v${r.version}.pdf`, { doc: r.doc }))}>
                            {r.status === "signed" ? "Download" : "Draft"}
                          </Btn>
                        </Td>
                      </tr>
                    );
                  })}
                </Tb>
              )}
            </Panel>
          </Sec>

          <Sec>
            <Panel title="If we ever change your terms" testId="paperwork-terms-promise">
              <div className="grid2" style={{ gap: 30 }}>
                <ul style={{ color: "var(--ink-3)", fontSize: 13.5, margin: 0, paddingLeft: 20, lineHeight: 1.95 }}>
                  <li>You get a <b>terms notice</b> by email, in writing, before anything changes</li>
                  <li>A new version appears here for signature</li>
                  <li>Your current agreement stays in force until you sign</li>
                  <li>Cases already submitted are paid on the <b>old</b> terms</li>
                  <li>Nothing applies retrospectively, ever</li>
                </ul>
                <div>
                  <div className="rail-t">What a notice looks like</div>
                  <div className="note" style={{ background: "var(--accent-soft)", borderColor: "rgba(125,169,255,.5)" }}>
                    <div className="note-h">
                      <span className="who-s">Connexis Group Ltd</span>
                      <span className="date">A terms notice</span>
                    </div>
                    <div style={{ color: "var(--ink-3)", fontSize: 13 }}>
                      We are reissuing your {title.toLowerCase()} as a new version. Your rate of{" "}
                      {liveT?.rate || ag?.terms?.rate || "—"} is unchanged unless the notice says otherwise.
                      Your current agreement remains in force until you sign.
                    </div>
                  </div>
                </div>
              </div>
            </Panel>
          </Sec>
        </PageBody>
      </div>
    </>
  );
}
